import { requisicaoAutenticada } from './http';

/** BECA IA (D-013) — o que a API devolve. Espelha `apps/api/src/modules/beca/beca.service.ts`. */

export interface SituacaoDaBeca {
  /** Há chave da Anthropic configurada no servidor. */
  readonly ligada: boolean;
  readonly modelo: string;
  readonly mes: { readonly perguntas: number; readonly custoUsd: number };
  readonly limiteMensalUsd: number | null;
}

export interface ConversaResumo {
  readonly id: string;
  readonly titulo: string;
  readonly atualizadaEm: string;
}

export type SituacaoDaAcao =
  'PENDENTE' | 'EXECUTANDO' | 'EXECUTADA' | 'CANCELADA' | 'FALHOU' | 'EXPIRADA';

/** O que conferir antes de confirmar (D-014). */
export interface DetalheDaAcao {
  readonly campos?: readonly { readonly rotulo: string; readonly valor: string }[];
  /** Um texto inteiro para ler antes — o corpo de um e-mail. */
  readonly texto?: string;
  /** O que acontece além do óbvio: "o fornecedor recebe o contrato para assinar". */
  readonly avisos?: readonly string[];
}

/** Uma ação que a BECA preparou: só acontece quando alguém confirma. */
export interface AcaoDaBeca {
  readonly id: string;
  readonly resumo: string;
  readonly detalhe: DetalheDaAcao | null;
  readonly situacao: SituacaoDaAcao;
  /** O que aconteceu, ou o porquê da falha. */
  readonly resultado: string | null;
  readonly validaAte: string;
  readonly decididaEm: string | null;
}

export interface MensagemDaBeca {
  readonly id: string;
  readonly autor: 'voce' | 'beca';
  readonly texto: string;
  /** As consultas que a BECA fez no Deca para responder — "Negociações do Pontual". */
  readonly consultas: readonly string[];
  /** As ações que ela preparou nesta resposta, cada uma com a sua confirmação. */
  readonly acoes: readonly AcaoDaBeca[];
  readonly criadaEm: string;
}

export interface ConversaDaBeca extends ConversaResumo {
  readonly mensagens: readonly MensagemDaBeca[];
}

export const CHAVE_DA_BECA = ['beca'] as const;

export function buscarSituacao(): Promise<SituacaoDaBeca> {
  return requisicaoAutenticada<SituacaoDaBeca>('/beca/situacao');
}

export function buscarConversas(): Promise<ConversaResumo[]> {
  return requisicaoAutenticada<ConversaResumo[]>('/beca/conversas');
}

export function buscarConversa(id: string): Promise<ConversaDaBeca> {
  return requisicaoAutenticada<ConversaDaBeca>(`/beca/conversas/${id}`);
}

/** Pergunta à BECA. Sem `conversaId`, começa uma conversa nova. Devolve a conversa inteira. */
export function perguntarABeca(entrada: {
  readonly texto: string;
  readonly conversaId?: string;
}): Promise<ConversaDaBeca> {
  return requisicaoAutenticada<ConversaDaBeca>('/beca/perguntas', {
    metodo: 'POST',
    corpo: entrada,
  });
}

export function excluirConversa(id: string): Promise<unknown> {
  return requisicaoAutenticada<unknown>(`/beca/conversas/${id}`, { metodo: 'DELETE' });
}

/** Confirma e executa uma ação preparada pela BECA. Falhar na execução volta como `FALHOU`, não como erro. */
export function confirmarAcao(id: string): Promise<AcaoDaBeca> {
  return requisicaoAutenticada<AcaoDaBeca>(`/beca/acoes/${id}/confirmacao`, {
    metodo: 'POST',
    corpo: {},
  });
}

export function cancelarAcao(id: string): Promise<AcaoDaBeca> {
  return requisicaoAutenticada<AcaoDaBeca>(`/beca/acoes/${id}/cancelamento`, {
    metodo: 'POST',
    corpo: {},
  });
}
